// Filter values
export const FILTER_ALL = "all" as const
export const FILTER_UNKNOWN = "__unknown__" as const
// Date format
export const DATE_FORMAT = "yyyy-MM-dd" as const

/** Stable DOM id prefix for each labeled form input. Sections render this on the input
 * wrapper; the missing-fields list in the footer uses it to scroll/focus the corresponding
 * input when the user clicks an entry. Pairs 1:1 with the keys of PlacementFormErrors. */
export const FIELD_ANCHOR_ID_PREFIX = "onboarding-field-" as const

/** User-facing labels for each PlacementFormErrors key. Used by the missing-fields summary
 * above the Proceed button so the list reads as field names ("Internal Company") rather than
 * raw keys ("internalCompanyId"). Keep in sync with the PlacementFormErrors keys. */
export const FIELD_LABELS = {
  internalCompanyId: "Internal Company",
  clientOrgId: "Client Organization",
  workerEmail: "Email address",
  profileTypeCode: "Profile Type",
  worker: "Worker",
  workerOrgId: "Worker Organization",
  lobCode: "Line of Business",
  startDate: "Start Date",
  endDate: "End Date",
  billRate: "Bill Rate",
  payRate: "Pay Rate",
  billCurrencyCode: "Bill Currency",
  payCurrencyCode: "Pay Currency",
} as const

// Preferred language codes used by the contact-level Preferred Language selector. The
// canonical FBO format is a full culture name ("en-CA" / "fr-CA"); the UI keys off the
// first two chars when normalizing arbitrary inputs (API-returned values, JWT-derived
// user locales) so any en/fr variant lands on one of these two options.
export const SUPPORTED_CULTURE_CODES = ["en-CA", "fr-CA"] as const
export type SupportedCultureCode = (typeof SUPPORTED_CULTURE_CODES)[number]

/** Normalize an arbitrary culture/language string to a SupportedCultureCode. Only the
 *  first two chars are consulted; anything starting with "fr" maps to "fr-CA" and
 *  anything else (including the empty/null cases) falls back to "en-CA". */
export function normalizeCultureCode(input: string | null | undefined): SupportedCultureCode {
  const head = (input ?? "").slice(0, 2).toLowerCase()
  return head === "fr" ? "fr-CA" : "en-CA"
}

/** FBO Contact.CultureId numeric values. Anything else is intentionally treated as
 *  null by cultureIdToCode below — callers default to English explicitly when they
 *  want a fallback, so unknown ids don't silently become English here. */
export const FBO_CULTURE_ID = {
  English: 48,
  French: 88,
} as const

/** Map an FBO Contact.CultureId to a SupportedCultureCode. Returns null when the id
 *  is missing or outside the recognized set — callers decide what to do with null
 *  (PreOnboard value next, then current user locale). */
export function cultureIdToCode(cultureId: number | null | undefined): SupportedCultureCode | null {
  if (cultureId === FBO_CULTURE_ID.French) return "fr-CA"
  if (cultureId === FBO_CULTURE_ID.English) return "en-CA"
  return null
}

// API-level numeric constants (from FBO code value tables, used by hooks for mapping)

// usr.CodeProfileWorkerT4Type
export const T4_TYPE = {
  T4Hourly: 1,
  T4Salaried: 2,
} as const

// usr.CodeUserStatus
export const USER_STATUS = {
  Active: 1,
  Inactive: 2,
  Draft: 5,
  PendingReview: 6,
  PendingChange: 7,
  Declined: 8,
  Recalled: 9,
  Terminated: 14,
} as const

// template.CodeTemplateStatus
export const TEMPLATE_STATUS = {
  Active: 1,
  InActive: 2,
} as const

// usr.CodeProfileStatus (only the IDs referenced in mapProfileStatus)
export const PROFILE_STATUS = {
  Active: 1,
  Inactive: 2,
  Draft: 4,
  PendingReview: 6,
  PendingChange: 7,
  Declined: 11,
  Recalled: 12,
  Terminated: 16,
} as const
